import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import { useNotification } from '../../contexts/NotificationContext';
import { useTheme, type Theme } from '../../contexts/ThemeContext';
import { useSettings } from '../../contexts/SettingsContext';
import { cn } from '../../lib/utils';
import { Label } from '../../components/ui/Label';
import { useUserSettings, useApplicationSettings, UserSettings } from '../admin/settings/useData';
import api from '../../services/api';
import SidebarLayoutPicker from './SidebarLayoutPicker';
import ThemePicker from './ThemePicker';
import BookmarksDisplayPicker, { BOOKMARK_MODE_TOAST } from './BookmarksDisplayPicker';
import { TableDensityPicker, PageSizePicker, type TableDensity } from './TableDensityPicker';
import { SidebarLayoutMode } from '../../config/sidebarLayout';
import type { BookmarksDisplayMode } from '../admin/settings/useData';

export type PreferencesSection = 'tables' | 'appearance' | 'display' | 'navigation' | 'all';

export type PreferencesProps = {
  /** Render without page chrome (used inside Account settings). */
  embedded?: boolean;
  /** When set, only that section is shown (all fields still saved together). */
  section?: PreferencesSection;
};

type SaveStatus = 'idle' | 'pending' | 'saving' | 'saved' | 'error';

const AUTOSAVE_MS = 450;

const Preferences: React.FC<PreferencesProps> = ({ embedded = false, section = 'all' }) => {
  const { showError, showSuccess } = useNotification();
  const { colors, theme, setTheme } = useTheme();
  const { appConfig } = useSettings();
  const { userSettings, isLoading, mutate: mutateUser } = useUserSettings();
  const { applicationSettings } = useApplicationSettings();

  const [userFormData, setUserFormData] = useState<Partial<UserSettings>>({});
  const [saveStatus, setSaveStatus] = useState<SaveStatus>('idle');
  const [hydrated, setHydrated] = useState(false);

  const formRef = useRef(userFormData);
  formRef.current = userFormData;
  const saveTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const savedClearTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const requestIdRef = useRef(0);

  const pageSizeOptions = useMemo(
    () =>
      applicationSettings?.pageSizeOptions?.length
        ? applicationSettings.pageSizeOptions
        : appConfig.table.pageSizeOptions,
    [applicationSettings?.pageSizeOptions, appConfig.table.pageSizeOptions],
  );

  useEffect(() => {
    if (!userSettings) return;
    // Don't clobber in-flight edits while autosaving.
    if (saveStatus === 'pending' || saveStatus === 'saving') return;

    const savedTheme =
      userSettings.theme && userSettings.theme !== 'auto'
        ? (userSettings.theme as Theme)
        : theme;
    setUserFormData({
      defaultPageSize: userSettings.defaultPageSize,
      tableDensity: userSettings.tableDensity,
      bookmarksDisplayMode: userSettings.bookmarksDisplayMode || 'sidebar',
      sidebarLayout: userSettings.sidebarLayout ?? null,
      theme: savedTheme,
    });
    setHydrated(true);
  }, [userSettings, saveStatus]); // eslint-disable-line react-hooks/exhaustive-deps -- theme only used as fallback seed

  useEffect(() => {
    return () => {
      if (saveTimerRef.current) clearTimeout(saveTimerRef.current);
      if (savedClearTimerRef.current) clearTimeout(savedClearTimerRef.current);
    };
  }, []);

  const persistPrefs = useCallback(
    async (payload: Partial<UserSettings>) => {
      const requestId = ++requestIdRef.current;
      setSaveStatus('saving');
      const nextTheme = (payload.theme as Theme | undefined) || theme;

      // Optimistic: chrome (bookmarks / nav) updates before the network returns.
      await mutateUser(
        (current) =>
          current
            ? ({
                ...current,
                ...payload,
                theme: nextTheme,
              } as UserSettings)
            : current,
        { revalidate: false },
      );
      window.dispatchEvent(new CustomEvent('sidebarPreferencesUpdated'));

      try {
        await api.patch('/settings/user', { ...payload, theme: nextTheme });
        if (requestId !== requestIdRef.current) return;
        setTheme(nextTheme);
        await mutateUser();
        setSaveStatus('saved');
        if (savedClearTimerRef.current) clearTimeout(savedClearTimerRef.current);
        savedClearTimerRef.current = setTimeout(() => {
          setSaveStatus((s) => (s === 'saved' ? 'idle' : s));
        }, 2000);
      } catch (error: any) {
        if (requestId !== requestIdRef.current) return;
        await mutateUser(); // roll back optimistic cache from server
        window.dispatchEvent(new CustomEvent('sidebarPreferencesUpdated'));
        setSaveStatus('error');
        showError(error?.response?.data?.message || 'Failed to save preferences');
      }
    },
    [mutateUser, setTheme, showError, theme],
  );

  const updatePrefs = useCallback(
    (patch: Partial<UserSettings>, opts?: { applyTheme?: Theme }) => {
      const prev = formRef.current;
      const next = { ...prev, ...patch };
      formRef.current = next;
      setUserFormData(next);
      if (opts?.applyTheme) setTheme(opts.applyTheme);

      if (
        patch.bookmarksDisplayMode != null &&
        patch.bookmarksDisplayMode !== prev.bookmarksDisplayMode
      ) {
        const mode = patch.bookmarksDisplayMode as BookmarksDisplayMode;
        showSuccess(BOOKMARK_MODE_TOAST[mode] ?? 'Bookmarks placement updated');
      }

      setSaveStatus('pending');
      if (saveTimerRef.current) clearTimeout(saveTimerRef.current);
      saveTimerRef.current = setTimeout(() => {
        void persistPrefs(formRef.current);
      }, AUTOSAVE_MS);
    },
    [persistPrefs, setTheme, showSuccess],
  );

  const show = (id: Exclude<PreferencesSection, 'all'>) => section === 'all' || section === id;
  /** Combined workspace view needs subsection titles; single-section embeds do not. */
  const showSectionHeading = section === 'all' || !embedded;

  const statusLabel =
    saveStatus === 'pending'
      ? 'Pending save…'
      : saveStatus === 'saving'
        ? 'Saving…'
        : saveStatus === 'saved'
          ? 'Saved'
          : saveStatus === 'error'
            ? 'Save failed — try again'
            : 'Changes save automatically';

  if (isLoading && !userSettings) {
    return (
      <div className={cn('flex items-center justify-center', embedded ? 'min-h-[20vh]' : 'min-h-[40vh]')}>
        <div className={colors.textSecondary}>Loading preferences...</div>
      </div>
    );
  }

  const form = (
    <div className={cn('relative rounded-2xl border', colors.border, colors.surface)}>
      <div className="space-y-8 p-5 pb-24 sm:p-6 sm:pb-24">
        {show('tables') && (
          <section className="space-y-6">
            {showSectionHeading && (
              <div className="space-y-1">
                <h3 className={cn('text-md font-medium', colors.text)}>Tables</h3>
                <p className={cn('text-xs', colors.textMuted)}>
                  Default list and grid behaviour across modules
                </p>
              </div>
            )}

            <div className="space-y-3">
              <div className="flex items-baseline justify-between gap-3">
                <Label>Default page size</Label>
                <p className={cn('text-xs', colors.textMuted)}>Rows loaded per page</p>
              </div>
              <PageSizePicker
                value={userFormData.defaultPageSize || appConfig.table.defaultPageSize || 200}
                options={pageSizeOptions}
                onChange={(next) => updatePrefs({ defaultPageSize: next })}
              />
            </div>

            <div className="space-y-3">
              <div className="flex items-baseline justify-between gap-3">
                <Label>Row density</Label>
                <p className={cn('text-xs', colors.textMuted)}>How tightly list rows are spaced</p>
              </div>
              <TableDensityPicker
                value={(userFormData.tableDensity as TableDensity) || 'normal'}
                onChange={(next) => updatePrefs({ tableDensity: next })}
              />
            </div>
          </section>
        )}

        {show('appearance') && (
          <section className={cn('space-y-4', section === 'all' && `pt-6 border-t ${colors.border}`)}>
            {showSectionHeading && (
              <div className="space-y-1">
                <h3 className={cn('text-md font-medium', colors.text)}>Appearance</h3>
                <p className={cn('text-xs', colors.textMuted)}>
                  Color theme for ATLAS (also available from the top bar)
                </p>
              </div>
            )}

            <div className="space-y-3">
              <div className="flex items-baseline justify-between gap-3">
                <Label>Theme</Label>
                <p className={cn('text-xs', colors.textMuted)}>Applies instantly · auto-saves</p>
              </div>
              <ThemePicker
                value={(userFormData.theme as Theme | undefined) || theme}
                onChange={(next) => updatePrefs({ theme: next }, { applyTheme: next })}
              />
            </div>
          </section>
        )}

        {show('display') && (
          <section className={cn('space-y-4', section === 'all' && `pt-6 border-t ${colors.border}`)}>
            {showSectionHeading && (
              <div className="space-y-1">
                <h3 className={cn('text-md font-medium', colors.text)}>Display</h3>
                <p className={cn('text-xs', colors.textMuted)}>
                  Bookmarks placement across the app
                </p>
              </div>
            )}

            <div className="space-y-3">
              <div className="flex items-baseline justify-between gap-3">
                <Label>Bookmarks</Label>
                <p className={cn('text-xs', colors.textMuted)}>Auto-saves</p>
              </div>
              <BookmarksDisplayPicker
                value={(userFormData.bookmarksDisplayMode as BookmarksDisplayMode) || 'sidebar'}
                onChange={(next) => updatePrefs({ bookmarksDisplayMode: next })}
              />
            </div>
          </section>
        )}

        {show('navigation') && (
          <section className={cn('space-y-4', section === 'all' && `pt-6 border-t ${colors.border}`)}>
            {showSectionHeading && (
              <div className="space-y-1">
                <h3 className={cn('text-md font-medium', colors.text)}>Navigation</h3>
                <p className={cn('text-xs', colors.textMuted)}>Sidebar and main menu layout</p>
              </div>
            )}

            <div className="space-y-3">
              <div className="flex items-baseline justify-between gap-3">
                <Label>Layout</Label>
                <p className={cn('text-xs', colors.textMuted)}>Auto-saves</p>
              </div>
              <SidebarLayoutPicker
                value={userFormData.sidebarLayout as SidebarLayoutMode | null | undefined}
                orgDefault={
                  (applicationSettings?.sidebarStyle as SidebarLayoutMode) ||
                  'classic'
                }
                onChange={(layout) => updatePrefs({ sidebarLayout: layout })}
              />
            </div>
          </section>
        )}
      </div>

      {/* Sticky status bar — always visible while scrolling Workspace */}
      <div
        className={cn(
          'sticky bottom-0 z-10 flex items-center justify-between gap-3 border-t px-5 py-3 sm:px-6',
          colors.border,
          colors.surface,
          'bg-opacity-95 backdrop-blur-sm dark:bg-opacity-95',
        )}
      >
        <div className="flex min-w-0 items-center gap-2">
          <span
            className={cn(
              'inline-flex h-2 w-2 shrink-0 rounded-full',
              saveStatus === 'saving' || saveStatus === 'pending'
                ? 'animate-pulse bg-amber-400'
                : saveStatus === 'saved'
                  ? 'bg-emerald-500'
                  : saveStatus === 'error'
                    ? 'bg-rose-500'
                    : 'bg-slate-300 dark:bg-slate-600',
            )}
            aria-hidden
          />
          <p
            className={cn(
              'truncate text-xs font-medium',
              saveStatus === 'error' ? 'text-rose-600 dark:text-rose-400' : colors.textMuted,
            )}
            role="status"
            aria-live="polite"
          >
            {hydrated ? statusLabel : 'Loading…'}
          </p>
        </div>
        {saveStatus === 'error' && (
          <button
            type="button"
            onClick={() => void persistPrefs(formRef.current)}
            className={cn(
              'shrink-0 rounded-lg px-3 py-1.5 text-xs font-semibold transition-colors',
              colors.primary,
              colors.primaryText,
            )}
          >
            Retry
          </button>
        )}
      </div>
    </div>
  );

  if (embedded) return form;

  return (
    <div className="h-full overflow-y-auto">
      <div className="mx-auto max-w-3xl space-y-6 p-6">{form}</div>
    </div>
  );
};

export default Preferences;
